import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sprout, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlantCard } from '@/components/plants/PlantCard';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useAuthGuard } from '@/hooks/useAuthGuard';

interface PlantIdentification {
  id: string;
  image_url: string;
  popular_name: string;
  scientific_name: string;
  planting_season: string;
  soil_type: string;
  sunlight_needs: string;
  water_needs: string;
  cultivation_tips: string;
  created_at: string;
}

export default function Collection() {
  const navigate = useNavigate();
  const { user, isAuthenticated, handleAuthError } = useAuthGuard();
  const [plants, setPlants] = useState<PlantIdentification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchPlants = useCallback(async (isRefresh = false) => {
    // Verify authentication before fetching
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    if (isRefresh) setRefreshing(true);
    
    try {
      const { data, error } = await supabase
        .from('plant_identifications')
        .select('*')
        .eq('user_id', user?.id) // Explicit user_id filter as extra security layer
        .order('created_at', { ascending: false });

      if (error) {
        // Check if it's an auth error
        const wasAuthError = await handleAuthError(error);
        if (wasAuthError) return;
        
        console.error('Error fetching plants:', error);
        toast.error('Erro ao carregar coleção');
        return;
      }

      setPlants(data || []);
      if (isRefresh) toast.success('Coleção atualizada');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar coleção');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id, isAuthenticated, handleAuthError]);

  useEffect(() => {
    if (user) {
      fetchPlants();
    } else {
      setLoading(false);
      setPlants([]);
    }
  }, [user, fetchPlants]);

  const handleDelete = async (id: string) => {
    // Verify authentication before deleting
    if (!isAuthenticated()) {
      toast.error('Você precisa estar logado para remover plantas.');
      return;
    }

    try {
      const { error } = await supabase
        .from('plant_identifications')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id); // Explicit user_id filter as extra security layer

      if (error) {
        // Check if it's an auth error
        const wasAuthError = await handleAuthError(error);
        if (wasAuthError) return;
        
        console.error('Error deleting plant:', error);
        toast.error('Erro ao remover planta');
        return;
      }

      setPlants(plants.filter((p) => p.id !== id));
      toast.success('Planta removida da coleção');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao remover planta');
    }
  };

  const RefreshButton = (
    <Button 
      variant="ghost" 
      size="icon" 
      onClick={() => fetchPlants(true)}
      disabled={refreshing}
      className="h-10 w-10"
    >
      <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
    </Button>
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      <MobileHeader title="Minha Coleção" showLogo rightElement={RefreshButton} />

      <main className="px-4 py-4">
        {loading ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-36 xs:h-32 w-full rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : plants.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Sprout className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-base font-semibold text-foreground mb-2">
              Sua coleção está vazia
            </h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
              Comece identificando plantas para construir sua coleção pessoal
            </p>
            <Button onClick={() => navigate('/identify')} className="gradient-nature text-primary-foreground h-11 press-scale touch-target">
              <Plus className="mr-2 h-4 w-4" />
              Identificar primeira planta
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {plants.length} {plants.length === 1 ? 'planta' : 'plantas'} na sua coleção
            </p>
            
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
              {plants.map((plant) => (
                <PlantCard
                  key={plant.id}
                  id={plant.id}
                  imageUrl={plant.image_url}
                  popularName={plant.popular_name || 'Planta não identificada'}
                  scientificName={plant.scientific_name || 'Nome científico desconhecido'}
                  sunlightNeeds={plant.sunlight_needs || undefined}
                  waterNeeds={plant.water_needs || undefined}
                  onDelete={handleDelete}
                  onClick={() => navigate(`/plant/${plant.id}`)}
                />
              ))}
            </div>
          </>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
}
